// U1c-3 — SLink agent list. Token-free table over useSlinkAgents() (mirrors
// UsersPage styling), resolving site_id → site name via the existing useSites().
import { Link } from "react-router-dom";
import { PlusIcon } from "lucide-react";

import { useSlinkAgents, type SlinkAgentListItem } from "@/api/agents";
import { useSites } from "@/api/sites";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useTenantStore } from "@/store/tenant";

export default function SlinkAgentListPage() {
  const { data, isLoading, isError } = useSlinkAgents();
  const { data: sitesData } = useSites();
  const isSuperAdmin = useTenantStore((s) => s.isSuperAdmin);
  const currentTenantId = useTenantStore((s) => s.currentTenantId);
  const needsTenant = isSuperAdmin && !currentTenantId;

  const siteName = (id: string) =>
    sitesData?.items?.find((s) => s.id === id)?.name ?? id;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">SLink Agents</h1>
          <p className="text-sm text-muted-foreground">
            {data ? `${data.length} agents` : "Loading…"}
          </p>
        </div>
        <Button asChild>
          <Link to="/devices/slink/new">
            <PlusIcon className="mr-2 h-4 w-4" />
            Add SLink Device
          </Link>
        </Button>
      </div>

      {needsTenant && (
        <p className="text-sm text-muted-foreground">
          Select a tenant from the switcher to manage its devices.
        </p>
      )}
      {!needsTenant && isLoading && <p className="text-sm">Loading…</p>}
      {!needsTenant && isError && (
        <p className="text-sm text-destructive">Could not load agents.</p>
      )}

      {!needsTenant && data && data.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 text-left">Name</th>
                  <th className="px-4 py-3 text-left">Site</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Credential</th>
                  <th className="px-4 py-3 text-right">Key version</th>
                  <th className="px-4 py-3 text-right">Created</th>
                </tr>
              </thead>
              <tbody>
                {data.map((a: SlinkAgentListItem) => (
                  <tr key={a.id} className="border-b last:border-0">
                    <td className="px-4 py-3">{a.name}</td>
                    <td className="px-4 py-3">{siteName(a.site_id)}</td>
                    <td className="px-4 py-3">
                      <Badge
                        variant={a.status === "connected" ? "online" : "offline"}
                      >
                        {a.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">
                      {a.credential_type}
                    </td>
                    <td className="px-4 py-3 text-right">{a.token_version}</td>
                    <td className="px-4 py-3 text-right text-xs text-muted-foreground">
                      {new Date(a.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {!needsTenant && data && data.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No SLink agents yet. Use “Add SLink Device” to register one.
        </p>
      )}
    </div>
  );
}
